import { motion } from "framer-motion";
import { Shield, BarChart3, Zap, Users, TrendingUp, LineChart } from "lucide-react";
import { SupportLayout } from "@/components/support-layout";

const GOLD = "#C9A867";
const APP_URL = import.meta.env.VITE_FRONTEND_URL || "https://app.nordex.trade";

const values = [
  {
    icon: BarChart3,
    title: "Performance Lab Pro\u2122",
    description:
      "Full trading analytics built right into the platform. Win rate, P&L breakdowns, drawdown tracking, session analysis \u2014 all automatic, all real-time. No third-party tools needed.",
  },
  {
    icon: Shield,
    title: "Non-Custodial & Secure",
    description:
      "You always control your funds. We never hold your assets and never have access to them. Trade with the confidence that your capital is yours \u2014 at all times.",
  },
  {
    icon: Zap,
    title: "Lightning-Fast Execution",
    description:
      "Sub-second order execution with up to 100x leverage. Built on high-performance infrastructure designed for serious traders who need speed and reliability.",
  },
  {
    icon: LineChart,
    title: "Professional-Grade Tools",
    description:
      "Advanced charting, multiple order types, real-time market data, and a clean interface that doesn\u2019t get in your way. Everything a professional trader expects, without the clutter.",
  },
];

export const About = () => {
  return (
    <SupportLayout maxWidth="max-w-5xl">
      {/* Hero section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-12"
      >
        <h1 className="text-3xl font-bold mb-3 tracking-tight">
          Trading Built Different
        </h1>
        <p className="text-[15px] leading-relaxed text-muted-foreground max-w-xl">
          We're traders and builders from Scandinavia. We got tired of platforms
          that gave us zero insight into our own performance — so we built one
          that does.
        </p>
      </motion.div>

      {/* The Gap in the Market */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-12"
      >
        <h2 className="text-2xl font-semibold mb-6">The Gap We Saw</h2>
        <p className="text-[15px] leading-relaxed text-muted-foreground mb-4">
          If you wanted to properly track your trading performance before, you
          were on your own. You'd connect a third-party tool to your exchange via
          API, hope it pulled the right data, then manually fill in the gaps —
          logging entries in spreadsheets, calculating your own P&L, trying to
          figure out what was actually working and what wasn't.
        </p>
        <p className="text-[15px] leading-relaxed text-muted-foreground">
          Even then, you'd only get a fraction of the picture. Limited history,
          missing metrics, and data that didn't update in real time. No exchange
          gave you proper analytics as part of the platform. We thought that was
          a problem worth solving.
        </p>
      </motion.section>

      {/* Performance Lab Pro */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="mb-12 p-6 border border-border rounded-lg bg-card"
      >
        <h2 className="text-2xl font-semibold mb-6">Performance Lab Pro\u2122</h2>
        <p className="text-[15px] leading-relaxed text-muted-foreground mb-6">
          This is what makes Nordex different. Performance Lab Pro is a full
          analytics suite built directly into the trading platform — not bolted
          on, not a third-party integration. Every trade you make is
          automatically tracked, analyzed, and broken down into actionable
          insights.
        </p>
        <div
          className="border-l-4 pl-6 py-2"
          style={{ borderColor: GOLD }}
        >
          <p className="text-[15px] font-medium mb-2">
            No limits on data. No manual input. No external tools required.
          </p>
          <p className="text-sm text-muted-foreground">
            Win rate, profit factor, average hold time, drawdown curves,
            session-by-session breakdowns, pair performance — it's all there.
            Updated in real time. No other trading platform gives you this level
            of analytics out of the box.
          </p>
        </div>
      </motion.section>

      {/* What Sets Us Apart */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="mb-12"
      >
        <h2 className="text-2xl font-semibold mb-6">What Sets Us Apart</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {values.map((value, index) => (
            <motion.div
              key={value.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + index * 0.1 }}
              className="p-5 border border-border rounded-lg bg-card"
            >
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                style={{
                  background: `${GOLD}15`,
                  border: `1px solid ${GOLD}30`,
                }}
              >
                <value.icon className="h-5 w-5" style={{ color: GOLD }} />
              </div>
              <h3 className="font-semibold mb-2">{value.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {value.description}
              </p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Built for Everyone */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="mb-12"
      >
        <h2 className="text-2xl font-semibold mb-6">Built for Everyone</h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-2 h-2 rounded-full"
                style={{ background: GOLD }}
              />
              <h3 className="font-semibold">For Beginners</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              New to crypto trading? Nordex is designed so you can get started in
              minutes — connect your wallet or sign up with email, and you're in.
              Clean interface, clear guidance, and analytics that help you learn
              from every trade you make.
            </p>
          </div>
          <div>
            <div className="flex items-center gap-3 mb-3">
              <div
                className="w-2 h-2 rounded-full"
                style={{ background: GOLD }}
              />
              <h3 className="font-semibold">For Professionals</h3>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Advanced charting, multiple order types, real-time data, and
              Performance Lab Pro giving you the edge. Everything you need to
              trade at a professional level — without the bloat and complexity of
              legacy platforms.
            </p>
          </div>
        </div>
      </motion.section>

      {/* Who We Are */}
      <motion.section
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="mb-12 p-6 border border-border rounded-lg bg-card"
      >
        <div className="flex items-center gap-3 mb-4">
          <Users className="h-6 w-6" style={{ color: GOLD }} />
          <h2 className="text-2xl font-semibold">Who We Are</h2>
        </div>
        <p className="text-[15px] leading-relaxed text-muted-foreground mb-4">
          We're traders and developers based in Denmark. We've spent years
          trading on every major platform — and we kept running into the same
          problem: great execution, zero insight into our own performance. So we
          built the platform we wished existed.
        </p>
        <p className="text-[15px] leading-relaxed text-muted-foreground">
          Nordex is what happens when traders build for traders. Scandinavian
          design meets serious trading infrastructure — clean, fast, and honest.
          No gimmicks, just the tools you actually need.
        </p>
      </motion.section>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.9 }}
        className="mb-12 p-8 border border-border rounded-lg bg-card text-center"
      >
        <h3 className="text-xl font-semibold mb-3">See It for Yourself</h3>
        <p className="text-sm text-muted-foreground mb-6 max-w-xl mx-auto">
          Launch the platform, make a trade, and check your Performance Lab.
          You'll see why traders are switching to Nordex.
        </p>
        <a
          href={APP_URL}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white transition-all hover:scale-105"
          style={{ background: GOLD }}
        >
          Start Trading
          <TrendingUp className="h-4 w-4" />
        </a>
      </motion.div>

    </SupportLayout>
  );
};
